import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

const moneyFormatFallback = '${{amount}}';
const suncoRecentlyViewedKey = 'suncoViewedProducts';
const suncoSessionCollectionKey = 'suncoLastCollection';
const suncoSessionCollectionTrailKey = 'suncoCollectionTrail';

function formatMoney(cents, format = moneyFormatFallback) {
  if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);

  const value = Number(cents || 0) / 100;
  return format
    .replace('{{amount}}', value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    .replace('{{amount_no_decimals}}', Math.round(value).toLocaleString('en-US'));
}

let suncoPdpCartSubtotalRequest = null;

function parseJsonScript(element) {
  if (!element?.textContent) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (_error) {
    return null;
  }
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

function setupGroupedAddons(root = document) {
  root.querySelectorAll('[data-sunco-grouped-addon]').forEach((row) => {
    if (row.dataset.suncoGroupedReady === 'true') return;
    row.dataset.suncoGroupedReady = 'true';

    const group = row.closest('.sunco-grouped-addons');
    const toggle = row.querySelector('[data-sunco-grouped-toggle]');
    const panel = row.querySelector('[data-sunco-grouped-panel]');
    const select = row.querySelector('[data-sunco-grouped-variant-select]');
    const qty = row.querySelector('[data-sunco-grouped-qty]');
    const summaryPrice = row.querySelector('[data-sunco-grouped-summary-price]');
    const image = row.querySelector('[data-sunco-grouped-image]');
    const addButton = row.querySelector('[data-sunco-grouped-add]');
    const status = row.querySelector('[data-sunco-grouped-status]');
    const defaultButtonLabel = addButton?.dataset.suncoGroupedDefaultLabel || addButton?.textContent?.trim() || '+ Add';

    const closeRow = (targetRow) => {
      const targetToggle = targetRow.querySelector('[data-sunco-grouped-toggle]');
      const targetPanel = targetRow.querySelector('[data-sunco-grouped-panel]');
      targetRow.classList.remove('sunco-grouped-addons__item--expanded');
      targetToggle?.setAttribute('aria-expanded', 'false');
      if (targetPanel) targetPanel.hidden = true;
    };

    const openRow = () => {
      group?.querySelectorAll('[data-sunco-grouped-addon]').forEach((otherRow) => {
        if (otherRow !== row) closeRow(otherRow);
      });
      row.classList.add('sunco-grouped-addons__item--expanded');
      toggle?.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
    };

    const toggleRow = () => {
      if (toggle?.getAttribute('aria-expanded') === 'true') {
        closeRow(row);
      } else {
        openRow();
      }
    };

    toggle?.addEventListener('click', toggleRow);

    if (!qty) return;

    const updateAddButtonLabel = () => {
      if (!(addButton instanceof HTMLElement)) return;
      if (addButton.disabled) return;
      const cents = Number(addButton.dataset.suncoGroupedPriceCents || 0);
      const quantity = Math.max(Number(qty.value || 1), 1);
      if (!cents) {
        addButton.textContent = defaultButtonLabel;
        return;
      }
      addButton.textContent = `Add ${quantity} · ${formatMoney(cents * quantity)}`;
    };

    const submitAddon = async (event) => {
      event.preventDefault();

      const variantId = Number(qty.dataset.variantId || addButton?.dataset.variantId);
      const quantity = Math.max(Number(qty.value || 1), 1);
      const sourceId = addButton?.id || 'sunco-grouped-addon-row';
      const sections = getCartSectionIds();

      if (!variantId || !(addButton instanceof HTMLElement)) return;

      addButton.setAttribute('disabled', 'disabled');
      addButton.textContent = 'Adding...';
      if (status) status.textContent = '';

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity }], sections }),
        });
        const payload = await response.json();

        if (payload.status) {
          const message = payload.message || 'Unable to add accessory to cart.';
          if (status) status.textContent = message;
          document.dispatchEvent(new CartErrorEvent(sourceId, message, payload.description, payload.errors));
          return;
        }

        addButton.dataset.added = 'true';
        addButton.textContent = 'Added';
        if (status) status.textContent = 'Added to cart';
        document.dispatchEvent(
          new CartAddEvent(undefined, sourceId, {
            source: 'sunco-grouped-addon-row',
            itemCount: quantity,
            productId: row.dataset.suncoGroupedProductId,
            sections: payload.sections,
          })
        );
      } catch (errorObject) {
        console.error(errorObject);
        if (status) status.textContent = 'Unable to add accessory to cart.';
        document.dispatchEvent(new CartErrorEvent(sourceId, 'Unable to add accessory to cart.', errorObject, errorObject));
      } finally {
        window.setTimeout(() => {
          addButton.dataset.added = 'false';
          addButton.removeAttribute('disabled');
          updateAddButtonLabel();
        }, 700);
      }
    };

    const update = () => {
      const option = select?.selectedOptions[0];
      qty.dataset.variantId = option?.value || qty.dataset.variantId || '';
      if (addButton) addButton.dataset.variantId = qty.dataset.variantId;
      if (addButton && option?.dataset.price) addButton.dataset.suncoGroupedPriceCents = option.dataset.price;
      if (summaryPrice && option?.dataset.priceFormatted) summaryPrice.textContent = option.dataset.priceFormatted;
      if (image && option?.dataset.image) image.src = option.dataset.image;
      const detailsLink = row.querySelector('[data-sunco-grouped-details-link]');
      if (detailsLink && option?.dataset.url) detailsLink.href = option.dataset.url;
      if (status) status.textContent = '';
      updateAddButtonLabel();
    };

    select?.addEventListener('change', update);
    qty.addEventListener('change', updateAddButtonLabel);
    qty.addEventListener('input', updateAddButtonLabel);
    addButton?.addEventListener('click', submitAddon);
    update();
  });
}

function normalizedOptionName(value) {
  return String(value || '').replace(':', '').trim().toLowerCase();
}

function variantOptionLabel(optionElement) {
  const legend = optionElement.querySelector('legend');
  if (legend) {
    const textNode = Array.from(legend.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    return normalizedOptionName(textNode?.textContent || legend.textContent);
  }

  return normalizedOptionName(optionElement.textContent?.split('\n')[0]);
}

function setupCombinedListingNativePicker(root = document) {
  root.querySelectorAll('.combined-listing-options[data-cross-options]').forEach((listing) => {
    const hiddenOptions = String(listing.dataset.crossOptions || '')
      .split('||')
      .map(normalizedOptionName)
      .filter(Boolean);

    if (hiddenOptions.length === 0) return;

    const section = listing.closest('.shopify-section') || document;
    const hideNativeCrossOptions = () => {
      section.querySelectorAll('variant-picker .variant-option').forEach((option) => {
        const shouldHide = hiddenOptions.includes(variantOptionLabel(option));
        option.classList.toggle('sunco-native-cross-option-hidden', shouldHide);
        if (shouldHide) {
          option.setAttribute('aria-hidden', 'true');
        } else {
          option.removeAttribute('aria-hidden');
        }
      });
    };

    hideNativeCrossOptions();
    section.addEventListener(ThemeEvents.variantUpdate, () => {
      window.requestAnimationFrame(hideNativeCrossOptions);
    });
  });
}

function updateVariantSelectedLabel(target) {
  const fieldset = target?.closest?.('fieldset.variant-option');
  const output = fieldset?.querySelector('[data-sunco-selected-option-value]');
  const selected = fieldset?.querySelector('input:checked');

  if (output && selected instanceof HTMLInputElement) output.textContent = selected.value;
}

function syncColorSwatchVisualState(root = document) {
  root.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
    const labels = fieldset.querySelectorAll('.sunco-color-swatch-label');
    if (labels.length === 0) return;

    labels.forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      label.classList.toggle('is-selected', Boolean(input?.checked));
    });
  });
}

function setupColorSwatchVisualState(root = document) {
  syncColorSwatchVisualState(root);

  if (document.documentElement.dataset.suncoColorSwatchVisualStateReady === 'true') return;
  document.documentElement.dataset.suncoColorSwatchVisualStateReady = 'true';

  document.addEventListener('change', (event) => {
    const fieldset = event.target?.closest?.('fieldset.variant-option');
    if (!fieldset?.querySelector?.('.sunco-color-swatch-label')) return;
    syncColorSwatchVisualState(fieldset);
  });

  document.addEventListener('click', (event) => {
    const label = event.target?.closest?.('variant-picker .sunco-color-swatch-label');
    const fieldset = label?.closest?.('fieldset.variant-option');
    if (!label || !fieldset) return;

    fieldset.querySelectorAll('.sunco-color-swatch-label').forEach((item) => {
      item.classList.toggle('is-selected', item === label);
    });
  });

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    window.requestAnimationFrame(() => {
      syncColorSwatchVisualState(event.target?.closest?.('.shopify-section') || document);
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    syncColorSwatchVisualState(event.target || document);
  });
}

function setupVariantSelectedLabels(root = document) {
  root.querySelectorAll('variant-picker fieldset.variant-option').forEach((fieldset) => {
    updateVariantSelectedLabel(fieldset.querySelector('input:checked'));
  });

  if (document.documentElement.dataset.suncoVariantSelectedLabelsReady === 'true') return;
  document.documentElement.dataset.suncoVariantSelectedLabelsReady = 'true';

  document.addEventListener('change', (event) => {
    if (event.target?.matches?.('variant-picker fieldset.variant-option input[type="radio"]')) {
      updateVariantSelectedLabel(event.target);
    }
  });

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const scope = event.target?.closest?.('.shopify-section') || document;
    scope.querySelectorAll('variant-picker fieldset.variant-option input:checked').forEach(updateVariantSelectedLabel);
  });
}

function setupVariantAnnouncements() {
  if (document.documentElement.dataset.suncoVariantAnnouncementsReady === 'true') return;
  document.documentElement.dataset.suncoVariantAnnouncementsReady = 'true';

  const status = document.createElement('span');
  status.className = 'visually-hidden';
  status.dataset.suncoVariantStatus = '';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');
  document.body.append(status);

  let announcementTimer = 0;

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const picker = event.target?.closest?.('variant-picker');
    if (!picker || picker.closest('product-card, quick-add-dialog')) return;

    const variant = event.detail?.resource;
    if (!variant) return;

    const parts = [];
    const variantTitle = String(variant.public_title || variant.title || '').trim();

    if (variantTitle && variantTitle.toLowerCase() !== 'default title') {
      parts.push(`${variantTitle} selected`);
    }

    if (Number.isFinite(Number(variant.price))) {
      const moneyFormat =
        document.querySelector('[data-sunco-unit-price]')?.dataset.moneyFormat || moneyFormatFallback;
      parts.push(`Price ${formatMoney(Number(variant.price), moneyFormat)}`);

      const packMatch = variantTitle.match(/(?:^|\/)\s*(\d+)\s*-?\s*pack(?:\s*\/|$)/i);
      const packSize = Number.parseInt(packMatch?.[1] || '', 10);
      if (packSize > 1) {
        parts.push(`${formatMoney(Math.round(Number(variant.price) / packSize), moneyFormat)} per unit`);
      }
    }

    if (variant.sku) parts.push(`SKU ${variant.sku}`);
    if (variant.available === true) parts.push('In stock');
    if (variant.available === false) parts.push('Unavailable');

    if (!parts.length) return;

    window.clearTimeout(announcementTimer);
    status.textContent = '';
    announcementTimer = window.setTimeout(() => {
      status.textContent = `${parts.join('. ')}.`;
    }, 40);
  });
}

function setupQuantitySteppers(root = document) {
  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sunco-qty-step]');
    if (!button) return;

    const wrapper = button.closest('[data-sunco-qty-control]');
    const input = wrapper?.querySelector('input');
    if (!input) return;

    event.preventDefault();
    const step = Number(button.dataset.suncoQtyStep || 0);
    const min = Number(input.min || 0);
    input.value = String(Math.max(Number(input.value || 0) + step, min));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

function setupBackInStock(root = document) {
  root.querySelectorAll('[data-sunco-bis]').forEach((triggerWrap) => {
    const section = triggerWrap.closest('.shopify-section') || document;
    const trigger = triggerWrap.querySelector('.klaviyo-bis-trigger');
    const initialVariant = parseJsonScript(triggerWrap.querySelector('[data-sunco-selected-variant]'));
    const productId = trigger?.dataset.productId || trigger?.dataset.product || '';

    const render = (variant) => {
      if (!variant) return;

      const isAvailable = Boolean(variant.available);
      triggerWrap.hidden = isAvailable;
      triggerWrap.classList.toggle('hidden', isAvailable);
      triggerWrap.dataset.variantId = variant.id || '';

      if (trigger && variant.id) {
        trigger.dataset.variantId = String(variant.id);
        trigger.dataset.variant = String(variant.id);
        if (productId) {
          trigger.dataset.productId = productId;
          trigger.dataset.product = productId;
        }
      }

      if (!isAvailable && trigger) {
        window.dispatchEvent(new CustomEvent('sunco:klaviyo-bis-trigger-ready', { detail: { trigger, variant } }));
      }
    };

    render(initialVariant);

    section.addEventListener(ThemeEvents.variantUpdate, (event) => {
      render(event.detail.resource);
    });
  });
}

function setupCartDrawerAfterProductAdd(root = document) {
  if (document.documentElement.dataset.suncoCartDrawerAfterAddReady === 'true') return;
  document.documentElement.dataset.suncoCartDrawerAfterAddReady = 'true';

  const openCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    const dialog = drawer?.querySelector('[data-testid="cart-drawer-dialog"]');
    if (!drawer || dialog?.open) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
    } else if (typeof drawer.showDialog === 'function') {
      drawer.showDialog();
    }
  };

  document.addEventListener('sunco:cart-add-success', (event) => {
    const source = event.detail?.source;
    if (source !== 'product-form-component') {
      return;
    }

    openCartDrawer();
  }, { capture: true });

  document.addEventListener(CartAddEvent.eventName, (event) => {
    const source = event.detail?.data?.source;
    if (
      event.detail?.data?.didError ||
      !['product-form-component', 'sunco-grouped-addon-row'].includes(source)
    ) {
      return;
    }

    openCartDrawer();
  }, { capture: true });
}

function setupStickyOverlayOffsets() {
  if (document.documentElement.dataset.suncoStickyOverlayOffsetsReady === 'true') return;
  document.documentElement.dataset.suncoStickyOverlayOffsetsReady = 'true';

  const selectors = ['#hubspot-messages-iframe-container', '#google-merchantwidget-iframe-wrapper'];
  const googleLeftOffset = '0px';
  let isApplyingOverlayOffset = false;

  const preserveBottom = (element) => {
    if (!(element instanceof HTMLElement) || element.dataset.suncoOriginalBottomSet === 'true') return;

    element.dataset.suncoOriginalBottomSet = 'true';
    element.dataset.suncoOriginalBottom = element.style.getPropertyValue('bottom');
    element.dataset.suncoOriginalBottomPriority = element.style.getPropertyPriority('bottom');
  };

  const markPositioned = (element) => {
    if (element instanceof HTMLElement) element.dataset.suncoWidgetPositioned = 'true';
  };

  const applyBottom = (element, bottom) => {
    if (!(element instanceof HTMLElement)) return;
    const bottomValue = String(bottom || '0px');
    element.dataset.suncoExpectedBottom = bottomValue;

    if (element.style.getPropertyValue('bottom') !== bottomValue || element.style.getPropertyPriority('bottom') !== 'important') {
      element.style.setProperty('bottom', bottomValue, 'important');
    }
  };

  const applyGoogleLeft = (element) => {
    if (!(element instanceof HTMLElement)) return;
    element.dataset.suncoExpectedLeft = googleLeftOffset;

    if (element.style.getPropertyValue('left') !== googleLeftOffset || element.style.getPropertyPriority('left') !== 'important') {
      element.style.setProperty('left', googleLeftOffset, 'important');
    }
  };

  const setHubspotLauncherVisibility = (hubspot, hidden) => {
    if (!(hubspot instanceof HTMLElement)) return;

    if (hidden) {
      hubspot.style.setProperty('opacity', '0', 'important');
      hubspot.style.setProperty('visibility', 'hidden', 'important');
      hubspot.style.setProperty('pointer-events', 'none', 'important');
      return;
    }

    hubspot.style.removeProperty('opacity');
    hubspot.style.removeProperty('visibility');
    hubspot.style.removeProperty('pointer-events');
  };

  const restoreBottom = (element) => {
    if (!(element instanceof HTMLElement) || element.dataset.suncoOriginalBottomSet !== 'true') return;

    const originalBottom = element.dataset.suncoOriginalBottom || '';
    const originalPriority = element.dataset.suncoOriginalBottomPriority || '';
    if (originalBottom) {
      element.style.setProperty('bottom', originalBottom, originalPriority);
    } else {
      element.style.removeProperty('bottom');
    }
  };

  const update = () => {
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    const isStickyActive = stickyBar?.getAttribute('data-stuck') === 'true';
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const stickyHeight = stickyBar instanceof HTMLElement ? stickyBar.getBoundingClientRect().height : 0;
    const previewBar = document.querySelector('#PBarNextFrameWrapper');
    const previewHeight = previewBar instanceof HTMLElement ? previewBar.getBoundingClientRect().height : 0;
    const bottomNav = document.querySelector('[data-sunco-mobile-bottom-nav]:not(.sunco-mobile-bottom-nav--product-hidden)');
    const hasMobileBottomNav = isMobile && bottomNav instanceof HTMLElement;
    const isBottomNavVisible = hasMobileBottomNav && !bottomNav.classList.contains('is-hidden');
    const bottomNavOffset = isBottomNavVisible ? Math.ceil(bottomNav.getBoundingClientRect().height + 10) : 0;
    const stickyOffset = isStickyActive ? Math.ceil(stickyHeight + previewHeight + 20) : 0;
    const offset = Math.max(stickyOffset, bottomNavOffset);
    const elements = selectors
      .map((selector) => document.querySelector(selector))
      .filter((element) => element instanceof HTMLElement);

    elements.forEach(preserveBottom);

    const hubspot = document.querySelector('#hubspot-messages-iframe-container');
    const google = document.querySelector('#google-merchantwidget-iframe-wrapper');
    const hubspotRect = hubspot instanceof HTMLElement ? hubspot.getBoundingClientRect() : null;
    const isExpandedChat = hubspotRect ? hubspotRect.width > 180 && hubspotRect.height > 180 : false;
    const isChatOpening = document.documentElement.classList.contains('sunco-hubspot-chat-requested');
    const shouldHideHubspotLauncher = !isExpandedChat && !isChatOpening;

    isApplyingOverlayOffset = true;

    if (!isMobile) {
      elements.forEach((element) => {
        if (element !== google) restoreBottom(element);
      });
      setHubspotLauncherVisibility(hubspot, shouldHideHubspotLauncher);
      if (hubspot instanceof HTMLElement && google instanceof HTMLElement) {
        applyBottom(google, getComputedStyle(hubspot).bottom);
        applyGoogleLeft(google);
        markPositioned(google);
      }
      elements.forEach((element) => {
        if (element !== google) markPositioned(element);
      });
      isApplyingOverlayOffset = false;
      return;
    }

    if (offset <= 20) {
      elements.forEach((element) => {
        if (element !== google) restoreBottom(element);
      });
      setHubspotLauncherVisibility(hubspot, shouldHideHubspotLauncher);

      if (hubspot instanceof HTMLElement && google instanceof HTMLElement) {
        applyBottom(google, getComputedStyle(hubspot).bottom);
        applyGoogleLeft(google);
        markPositioned(google);
      }
      elements.forEach((element) => {
        if (element !== google) markPositioned(element);
      });
      isApplyingOverlayOffset = false;
      return;
    }

    elements.forEach((element) => {
      if (offset > 20) {
        applyBottom(element, `${offset}px`);
        if (element === hubspot) {
          setHubspotLauncherVisibility(hubspot, shouldHideHubspotLauncher);
          if (isExpandedChat || isChatOpening) {
            element.style.setProperty('max-height', `calc(100dvh - ${offset}px - 20px)`, 'important');
            element.style.setProperty('overflow', 'hidden', 'important');
          } else {
            element.style.removeProperty('max-height');
            element.style.removeProperty('overflow');
          }
        }
        if (element === google) applyGoogleLeft(element);
        markPositioned(element);
      } else {
        restoreBottom(element);
        if (element === hubspot) setHubspotLauncherVisibility(hubspot, shouldHideHubspotLauncher);
        markPositioned(element);
      }
    });
    isApplyingOverlayOffset = false;
  };

  const observeSticky = () => {
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    if (!(stickyBar instanceof HTMLElement) || stickyBar.dataset.suncoOverlayObserverReady === 'true') return;

    stickyBar.dataset.suncoOverlayObserverReady = 'true';
    const observer = new MutationObserver(update);
    observer.observe(stickyBar, { attributes: true, attributeFilter: ['data-stuck'] });
  };

  const observeFloatingWidgets = () => {
    const google = document.querySelector('#google-merchantwidget-iframe-wrapper');
    if (!(google instanceof HTMLElement) || google.dataset.suncoStyleObserverReady === 'true') return;

    google.dataset.suncoStyleObserverReady = 'true';
    const observer = new MutationObserver(() => {
      if (isApplyingOverlayOffset) return;

      const expectedBottom = google.dataset.suncoExpectedBottom;
      const expectedLeft = google.dataset.suncoExpectedLeft;
      const bottomMatches = expectedBottom && google.style.getPropertyValue('bottom') === expectedBottom && google.style.getPropertyPriority('bottom') === 'important';
      const leftMatches = expectedLeft && google.style.getPropertyValue('left') === expectedLeft && google.style.getPropertyPriority('left') === 'important';

      if (bottomMatches && leftMatches) {
        return;
      }

      update();
    });
    observer.observe(google, { attributes: true, attributeFilter: ['style'] });
  };

  observeSticky();
  observeFloatingWidgets();
  update();

  let bodyObserverFrame = 0;
  const bodyObserver = new MutationObserver(() => {
    if (bodyObserverFrame) return;
    bodyObserverFrame = window.requestAnimationFrame(() => {
      bodyObserverFrame = 0;
      observeSticky();
      observeFloatingWidgets();
      update();
    });
  });
  bodyObserver.observe(document.body, { childList: true });

  window.addEventListener('resize', update);
  window.addEventListener('sunco:mobile-bottom-nav-toggle', update);
  window.addEventListener('sunco:floating-widgets-update', update);
  window.setTimeout(update, 800);
  window.setTimeout(update, 1800);
}

function setupSkuCopy(root = document) {
  root.addEventListener('click', async (event) => {
    const target = event.target.closest('[data-sunco-sku-copy]');
    if (!target) return;

    const sku = target.textContent.trim();
    if (!sku) return;

    try {
      await navigator.clipboard.writeText(sku);
      target.dataset.copied = 'true';
      window.setTimeout(() => {
        target.dataset.copied = 'false';
      }, 1400);
    } catch (_error) {
      const input = document.createElement('input');
      input.value = sku;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
  });
}

function quotePhoneDigits(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.slice(0, digits.startsWith('1') ? 11 : 10);
}

function formatQuotePhone(value) {
  const digits = quotePhoneDigits(value);
  const hasCountryCode = digits.startsWith('1');
  const nationalNumber = hasCountryCode ? digits.slice(1) : digits;
  let formatted = hasCountryCode ? '1' : '';

  if (!nationalNumber) return formatted;

  formatted += `${hasCountryCode ? ' ' : ''}(${nationalNumber.slice(0, 3)}`;
  if (nationalNumber.length < 3) return formatted;

  formatted += ')';
  if (nationalNumber.length === 3) return formatted;

  formatted += ` ${nationalNumber.slice(3, 6)}`;
  if (nationalNumber.length <= 6) return formatted;

  return `${formatted}-${nationalNumber.slice(6, 10)}`;
}

function quotePhoneCaretPosition(formatted, digitCount) {
  if (digitCount <= 0) return formatted.startsWith('(') ? 1 : 0;

  let seenDigits = 0;
  let position = formatted.length;

  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index])) seenDigits += 1;
    if (seenDigits === digitCount) {
      position = index + 1;
      break;
    }
  }

  while (position < formatted.length && /\D/.test(formatted[position])) position += 1;
  return position;
}

function setupBulkQuoteForm(root = document) {
  root.addEventListener('beforeinput', (event) => {
    const input = event.target.closest('[data-sunco-quote-phone]');
    if (!(input instanceof HTMLInputElement)) return;

    if (event.inputType === 'insertText' && event.data && /\D/.test(event.data)) {
      event.preventDefault();
    }
  });

  root.addEventListener('input', (event) => {
    const input = event.target.closest('[data-sunco-quote-phone]');
    if (!(input instanceof HTMLInputElement)) return;

    const currentValue = input.value;
    const currentCaret = input.selectionStart ?? currentValue.length;
    const digitsBeforeCaret = currentValue.slice(0, currentCaret).replace(/\D/g, '').length;
    const formatted = formatQuotePhone(currentValue);

    input.value = formatted;
    if (document.activeElement === input) {
      const nextCaret = quotePhoneCaretPosition(formatted, digitsBeforeCaret);
      input.setSelectionRange(nextCaret, nextCaret);
    }
  });

  root.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-sunco-quote-form]');
    if (!form) return;

    event.preventDefault();
    const message = form.parentElement?.querySelector('[data-sunco-quote-message]');
    const button = form.querySelector('button[type="submit"]');
    button?.setAttribute('disabled', 'disabled');

    try {
      const formData = new FormData(form);
      const phone = formData.get('phone');
      if (typeof phone === 'string') formData.set('phone', quotePhoneDigits(phone));

      await fetch('https://hooks.zapier.com/hooks/catch/5727789/o2bssaw/', {
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      });
      if (message) {
        message.innerHTML = '<strong>Thank you for your interest.</strong><br>One of our specialists will contact you shortly.';
      }
      form.style.display = 'none';
      if (typeof window.suncoTrackBulkInquiryConversion === 'function') {
        window.suncoTrackBulkInquiryConversion();
      } else if (window.gtag) {
        window.gtag('event', 'submit', { event_category: 'form', event_label: 'bulkforms' });
      }
    } finally {
      button?.removeAttribute('disabled');
    }
  });
}

function setupDiscountLearnMore(root = document) {
  root.addEventListener('click', (event) => {
    const target = event.target.closest('[data-sunco-open-quote]');
    if (!target) return;

    const quoteButton = document.querySelector('[data-sunco-quote-button]');
    quoteButton?.click();
  });
}

function setupUnitPrice(root = document) {
  root.querySelectorAll('[data-sunco-unit-price]').forEach((component) => {
    const data = parseJsonScript(component.querySelector('script[type="application/json"]'));
    if (!data) return;

    const hasPackOption = data.options?.some((name) => name.includes('Pack Size'));
    if (!hasPackOption) return;

    const output = component.querySelector('[data-sunco-unit-price-output]');
    const moneyFormat = component.dataset.moneyFormat || moneyFormatFallback;

    const render = (variant) => {
      if (!variant || !output) return;

      const parts = String(variant.title || '').split('/').map((part) => part.trim());
      if (parts.some((part) => part.includes('Single'))) {
        output.textContent = '';
        return;
      }

      const packPart = parts.find((part) => part.includes('Pack'));
      const pack = Number.parseInt(packPart, 10);
      if (!pack || pack <= 1) {
        output.textContent = '';
        return;
      }

      const unitPrice = Math.round(Number(variant.price) / pack);
      if (unitPrice === Number(variant.price)) {
        output.textContent = '';
        return;
      }

      output.textContent = `${formatMoney(unitPrice, moneyFormat)} / unit`;
    };

    render(data.selected_variant);

    component.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, (event) => {
      render(event.detail.resource);
    });
  });
}

function quantityInputForSubtotal(component) {
  const form = component.closest('form') || component.closest('product-form-component')?.querySelector('form');
  return form?.querySelector('input[name="quantity"]');
}

function quantityForSubtotal(component) {
  const input = quantityInputForSubtotal(component);
  return Math.max(Number(input?.value || 1), 1);
}

function validQuantityForSubtotal(component, requestedQuantity) {
  if (requestedQuantity <= 0) return 0;

  const input = quantityInputForSubtotal(component);
  const minimum = Math.max(Number(input?.min || input?.dataset.min || 1), 1);
  const parsedIncrement = Number(input?.step || 1);
  const increment = Number.isFinite(parsedIncrement) && parsedIncrement > 0 ? parsedIncrement : 1;
  const maximum = Number(input?.max || 0);
  const validQuantity =
    requestedQuantity <= minimum
      ? minimum
      : minimum + Math.ceil((requestedQuantity - minimum) / increment) * increment;

  return maximum > 0 && validQuantity > maximum ? 0 : validQuantity;
}

function volumeDiscountTiers(component, tableType = 'sunco') {
  const selector = tableType === 'sunco' ? '[data-sunco-volume-tiers]' : '[data-sunco-volume-tiers-other]';
  const tiers = parseJsonScript(component.querySelector(selector));
  if (!Array.isArray(tiers)) return [];

  return tiers
    .map((tier) => ({
      min: Number(tier.min || 0),
      label: String(tier.label || ''),
      rate: String(tier.rate || ''),
    }))
    .filter((tier) => tier.min > 0 && tier.rate);
}

function activeVolumeTier(total, tiers) {
  return tiers.reduce((active, tier) => (total >= tier.min ? tier : active), null);
}

function nextVolumeTier(total, tiers) {
  return tiers.find((tier) => total < tier.min) || null;
}

function volumeTierRatePercent(tier) {
  const rate = String(tier?.rate || '');
  const match = rate.match(/[\d.]+/);
  return match ? Number(match[0]) : 0;
}

function volumeDiscountForItems(price, quantity, tier) {
  const rate = volumeTierRatePercent(tier);
  if (price <= 0 || quantity <= 0 || rate <= 0) return 0;
  // Shopify allocates the whole-cent discount per unit before extending the line quantity.
  return Math.floor((price * rate) / 100) * quantity;
}

function shouldShowVolumeNudge(remaining, nextTier, productCount) {
  if (!nextTier || remaining <= 0) return false;

  const dynamicWindow = Math.round(nextTier.min * 0.1);
  const nudgeWindow = Math.min(Math.max(dynamicWindow, 10000), 50000);
  return remaining <= nudgeWindow || (productCount > 0 && productCount <= 3);
}

function productsNeededForTier(remaining, price) {
  if (remaining <= 0 || price <= 0) return 0;
  return Math.max(Math.ceil(remaining / price), 1);
}

function totalProductsNeededForTier(tierMinimum, currentSubtotal, price, component) {
  const requestedQuantity = productsNeededForTier(tierMinimum - currentSubtotal, price);
  return validQuantityForSubtotal(component, requestedQuantity);
}

function normalizeVendorName(value) {
  return String(value || '').trim().toLowerCase();
}

function isSuncoLightingVendor(vendor, component) {
  return normalizeVendorName(vendor) === normalizeVendorName(component?.dataset.suncoVendorName || 'Sunco Lighting');
}

function cartVolumeSubtotalGroups(cart, component) {
  return (cart?.items || []).reduce(
    (groups, item) => {
      const lineSubtotal = Number(item.original_line_price ?? item.line_price ?? item.final_line_price ?? 0);
      if (isSuncoLightingVendor(item.vendor, component)) {
        groups.sunco += lineSubtotal;
      } else {
        groups.other += lineSubtotal;
      }
      return groups;
    },
    { sunco: 0, other: 0 }
  );
}

function componentDiscountTableType(component) {
  const explicitType = component?.dataset.discountTableType;
  if (explicitType === 'sunco' || explicitType === 'other') return explicitType;

  return isSuncoLightingVendor(component?.dataset.productVendor, component) ? 'sunco' : 'other';
}

function updatePdpCartSubtotal(cart, root = document) {
  if (cart?.items_subtotal_price == null) return;

  root.querySelectorAll('[data-sunco-pdp-subtotal]').forEach((component) => {
    if (!(component instanceof HTMLElement)) return;
    const groups = cartVolumeSubtotalGroups(cart, component);
    component.dataset.cartSubtotalCents = String(cart.items_subtotal_price);
    component.dataset.cartSuncoSubtotalCents = String(groups.sunco);
    component.dataset.cartOtherSubtotalCents = String(groups.other);
    renderPdpSubtotal(component);
  });
}

async function refreshPdpCartSubtotal(root = document) {
  if (!root.querySelector('[data-sunco-pdp-subtotal]')) return;

  if (!suncoPdpCartSubtotalRequest) {
    suncoPdpCartSubtotalRequest = fetch('/cart.js', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .finally(() => {
        suncoPdpCartSubtotalRequest = null;
      });
  }

  const cart = await suncoPdpCartSubtotalRequest;
  updatePdpCartSubtotal(cart, root);
}

function renderVolumeSavingsBadge(badge, activeTier) {
  if (!(badge instanceof HTMLElement)) return;

  const nextRate = activeTier?.rate ? String(activeTier.rate) : '';
  const previousRate = badge.dataset.suncoVolumeRate || '';
  const hasRendered = badge.dataset.suncoVolumeRateReady === 'true';

  if (nextRate) {
    let rate = badge.querySelector('[data-sunco-volume-rate-value]');

    if (!(rate instanceof HTMLElement)) {
      rate = document.createElement('span');
      rate.className = 'sunco-pdp-subtotal__badge-rate';
      rate.dataset.suncoVolumeRateValue = '';
      badge.replaceChildren(rate, document.createTextNode(' Volume Savings'));
    }

    rate.textContent = nextRate;

    if (hasRendered && nextRate !== previousRate) {
      rate.classList.remove('sunco-pdp-subtotal__badge-rate--changing');
      void rate.offsetWidth;
      rate.classList.add('sunco-pdp-subtotal__badge-rate--changing');
      rate.addEventListener(
        'animationend',
        () => rate.classList.remove('sunco-pdp-subtotal__badge-rate--changing'),
        { once: true }
      );
    }
  } else {
    badge.textContent = 'Volume Savings';
  }

  badge.dataset.suncoVolumeRate = nextRate;
  badge.dataset.suncoVolumeRateReady = 'true';
}

function renderPdpSubtotal(component) {
  if (!(component instanceof HTMLElement)) return;

  const price = Number(component.dataset.variantPrice || 0);
  const threshold = Number(component.dataset.bulkThresholdCents || 50000);
  const moneyFormat = component.dataset.moneyFormat || moneyFormatFallback;
  const showItemTotal = component.dataset.showItemTotal !== 'false';
  const showCartTotal = component.dataset.showCartTotal === 'true';
  const quantity = quantityForSubtotal(component);
  const subtotal = price * quantity;
  const cartSubtotal = Number(component.dataset.cartSubtotalCents || 0);
  const discountTableType = componentDiscountTableType(component);
  const cartSuncoSubtotal = Number(component.dataset.cartSuncoSubtotalCents || 0);
  const cartOtherSubtotal = Number(component.dataset.cartOtherSubtotalCents || 0);
  const currentGroupSubtotal = discountTableType === 'sunco' ? cartSuncoSubtotal : cartOtherSubtotal;
  const projectedSubtotal = cartSubtotal + subtotal;
  const projectedVolumeBasis = currentGroupSubtotal + subtotal;
  const tiers = volumeDiscountTiers(component, discountTableType);
  const currentTier = activeVolumeTier(currentGroupSubtotal, tiers);
  const activeTier = activeVolumeTier(projectedVolumeBasis, tiers);
  const nextTier = nextVolumeTier(projectedVolumeBasis, tiers);
  const volumeSavings = volumeDiscountForItems(price, quantity, activeTier);
  const discountedSubtotal = subtotal - volumeSavings;
  const itemTotal = component.querySelector('[data-sunco-item-total]');
  const value = component.querySelector('[data-sunco-subtotal-value]');
  const compareValue = component.querySelector('[data-sunco-subtotal-compare]');
  const savingsValue = component.querySelector('[data-sunco-subtotal-save]');
  const saleMeta = component.querySelector('[data-sunco-subtotal-sale-meta]');
  const projectedTotal = component.querySelector('.sunco-pdp-subtotal__projected');
  const projectedValue = component.querySelector('[data-sunco-projected-value]');
  const eligible = component.querySelector('[data-sunco-subtotal-eligible]');
  const message = component.querySelector('[data-sunco-volume-message]');

  if (itemTotal instanceof HTMLElement) {
    const isDuplicateTotal = showCartTotal && projectedSubtotal === discountedSubtotal;
    const shouldHideItemTotal = !showItemTotal || isDuplicateTotal;
    itemTotal.hidden = shouldHideItemTotal;
    itemTotal.classList.toggle('hidden', shouldHideItemTotal);
  }
  if (projectedTotal instanceof HTMLElement) {
    projectedTotal.hidden = !showCartTotal;
    projectedTotal.classList.toggle('hidden', !showCartTotal);
  }
  if (value) value.textContent = formatMoney(discountedSubtotal, moneyFormat);
  if (saleMeta instanceof HTMLElement) {
    const hasSavings = volumeSavings > 0;
    saleMeta.hidden = !hasSavings;
    saleMeta.classList.toggle('hidden', !hasSavings);
    if (hasSavings) {
      if (compareValue) compareValue.textContent = formatMoney(subtotal, moneyFormat);
      if (savingsValue) savingsValue.textContent = `Save ${formatMoney(volumeSavings, moneyFormat)}`;
    }
  }
  if (projectedValue) projectedValue.textContent = formatMoney(projectedSubtotal, moneyFormat);
  if (eligible instanceof HTMLElement) {
    const isEligible = projectedVolumeBasis >= threshold;
    eligible.hidden = !isEligible;
    eligible.classList.toggle('hidden', !isEligible);
    renderVolumeSavingsBadge(eligible, activeTier);
  }
  if (message) {
    const unlocksTierWithSelectedQuantity =
      quantity === 1 && volumeTierRatePercent(activeTier) > volumeTierRatePercent(currentTier);
    const remaining = nextTier ? nextTier.min - projectedVolumeBasis : 0;
    const hasExistingCartSubtotal = currentGroupSubtotal > 0;
    const additionalProductsNeeded = productsNeededForTier(remaining, price);
    const totalProductsNeeded = totalProductsNeededForTier(
      nextTier?.min || 0,
      currentGroupSubtotal,
      price,
      component
    );
    const useIncrementalQuantity = quantity > 1;
    const describesNextTier = hasExistingCartSubtotal || useIncrementalQuantity;
    const productsNeeded = useIncrementalQuantity ? additionalProductsNeeded : totalProductsNeeded;
    const showNudge = shouldShowVolumeNudge(remaining, nextTier, productsNeeded);

    if (unlocksTierWithSelectedQuantity) {
      message.textContent = `Add 1 product to unlock ${currentTier ? 'next tier of ' : ''}volume savings.`;
      message.hidden = false;
      message.classList.remove('hidden');
    } else if (showNudge) {
      message.textContent =
        productsNeeded > 0 && useIncrementalQuantity
          ? `Add ${productsNeeded} more product${productsNeeded === 1 ? '' : 's'} to unlock next tier of volume savings.`
          : productsNeeded > 0
            ? `Add ${productsNeeded} product${productsNeeded === 1 ? '' : 's'} to unlock ${describesNextTier ? 'next tier of ' : ''}volume savings.`
          : describesNextTier
            ? `Add ${formatMoney(remaining, moneyFormat)} more to unlock next tier of volume savings.`
            : `Add ${formatMoney(remaining, moneyFormat)} more to unlock volume savings.`;
      message.hidden = false;
      message.classList.remove('hidden');
    } else {
      message.textContent = '';
      message.hidden = true;
      message.classList.add('hidden');
    }
  }
}

function setupPdpSubtotal(root = document) {
  root.querySelectorAll('[data-sunco-pdp-subtotal]').forEach(renderPdpSubtotal);
  refreshPdpCartSubtotal(root);

  if (document.documentElement.dataset.suncoPdpSubtotalReady === 'true') return;
  document.documentElement.dataset.suncoPdpSubtotalReady = 'true';

  const renderForForm = (form) => {
    form?.querySelectorAll('[data-sunco-pdp-subtotal]').forEach(renderPdpSubtotal);
  };

  document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
    if (!(event.target instanceof Element) || event.detail?.cartLine) return;
    const sticky = event.target.closest('sticky-add-to-cart');
    if (!sticky) {
      renderForForm(event.target.closest('form'));
      return;
    }

    // Sticky controls are outside the form. Wait for their native listener to
    // synchronize the main quantity, regardless of listener registration order.
    queueMicrotask(() => {
      if (!sticky.isConnected) return;
      const section = sticky.closest('.shopify-section');
      section?.querySelectorAll('product-form-component').forEach((form) => {
        if (form.dataset.productId === sticky.dataset.productId) renderForForm(form);
      });
    });
  });

  document.addEventListener('input', (event) => {
    if (event.target?.matches?.('input[name="quantity"]')) renderForForm(event.target.closest('form'));
  });

  document.addEventListener('change', (event) => {
    if (event.target?.matches?.('input[name="quantity"]')) renderForForm(event.target.closest('form'));
  });

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const scope = event.target?.closest?.('.shopify-section') || document;
    scope.querySelectorAll('[data-sunco-pdp-subtotal]').forEach((component) => {
      if (event.detail.resource?.price != null) component.dataset.variantPrice = String(event.detail.resource.price);
      renderPdpSubtotal(component);
    });
  });

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.didError) return;

    if (event.detail?.resource?.items_subtotal_price != null) {
      updatePdpCartSubtotal(event.detail.resource);
      return;
    }

    refreshPdpCartSubtotal();
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshPdpCartSubtotal();
  });
}

function setupRecentlyViewed(root = document) {
  const normalizeImage = (value) => {
    const image = value ? String(value) : '';
    if (image.startsWith('//')) return `${window.location.protocol}${image}`;
    return image;
  };

  const normalize = (product) => {
    if (product == null) return null;

    if (typeof product !== 'object') {
      return { id: String(product) };
    }

    if (product.id == null) return null;

    return {
      id: String(product.id),
      title: product.title ? String(product.title) : '',
      url: product.url ? String(product.url) : '',
      image: normalizeImage(product.image),
      price: product.price ? String(product.price) : '',
      card_html: product.card_html ? String(product.card_html) : '',
    };
  };

  const readViewed = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(suncoRecentlyViewedKey) || '[]');
      return Array.isArray(stored) ? stored.map(normalize).filter(Boolean) : [];
    } catch (_error) {
      return [];
    }
  };

  const writeCurrent = (currentProduct) => {
    const normalizedCurrent = normalize(currentProduct);
    if (!normalizedCurrent?.id) return;

    const viewed = readViewed().filter((product) => product.id !== normalizedCurrent.id);
    viewed.unshift(normalizedCurrent);
    localStorage.setItem(suncoRecentlyViewedKey, JSON.stringify(viewed.slice(0, 12)));
  };

  const writeViewed = (products) => {
    localStorage.setItem(suncoRecentlyViewedKey, JSON.stringify(products.map(normalize).filter(Boolean).slice(0, 12)));
  };

  const getCardFragment = (product, templateLookup, index) => {
    const template = templateLookup.get(String(product.id));
    const fragmentSource = template?.content ? template.content : null;
    let fragment = null;

    if (fragmentSource) {
      fragment = fragmentSource.cloneNode(true);
    } else if (product.card_html) {
      const storedTemplate = document.createElement('template');
      storedTemplate.innerHTML = product.card_html;
      fragment = storedTemplate.content.cloneNode(true);
    }

    if (!fragment) return null;

    const cards = fragment.querySelectorAll('[data-sunco-product-card]');
    if (cards.length === 0) return null;

    cards.forEach((card) => {
      card.dataset.suncoCardPosition = String(index + 1);
    });

    return fragment;
  };

  root.querySelectorAll('[data-sunco-recently-viewed]').forEach((section) => {
    const data = parseJsonScript(section.querySelector('[data-sunco-recently-config]')) || {};
    const currentCardTemplate = section.querySelector('[data-sunco-recent-current-card-template]');
    writeCurrent({
      ...(data.current_product || { id: data.current_id }),
      card_html: currentCardTemplate?.innerHTML || '',
    });

    const products = data.products || [];
    const productLookup = products.reduce((map, product) => {
      const normalizedProduct = normalize(product);
      if (normalizedProduct?.id) map.set(normalizedProduct.id, normalizedProduct);
      return map;
    }, new Map());
    const viewedProducts = readViewed()
      .filter((product) => product.id !== String(data.current_id))
      .map((product) => {
        if (product.title && product.url) return product;
        return productLookup.get(product.id);
      })
      .filter(Boolean)
      .slice(0, data.limit || 8);
    const grid = section.querySelector('[data-sunco-recent-grid]');
    if (!grid) return;

    grid.replaceChildren();

    if (viewedProducts.length === 0) {
      section.hidden = !data.design_mode;
      return;
    }

    const templateLookup = new Map(
      Array.from(section.querySelectorAll('[data-sunco-recent-card-template]')).map((template) => [
        String(template.dataset.suncoRecentCardTemplate || ''),
        template,
      ])
    );

    const renderViewedProducts = (products) => {
      grid.replaceChildren();

      products.forEach((product, index) => {
        const fragment = getCardFragment(product, templateLookup, index);
        if (fragment) grid.append(fragment);
      });

      if (grid.children.length === 0) {
        section.hidden = !data.design_mode;
        return;
      }

      if (window.SuncoPlp?.setupProductCards) {
        window.SuncoPlp.setupProductCards(section);
      }
      window.SuncoProductRails?.init?.(section);

      section.hidden = false;
    };

    renderViewedProducts(viewedProducts);

    const unresolvedProducts = viewedProducts.filter(
      (product, index) => !getCardFragment(product, templateLookup, index)
    );

    if (unresolvedProducts.length === 0) return;
  });
}

function humanizeCollectionHandle(handle) {
  const normalized = String(handle || '').trim().toLowerCase();
  const titleOverrides = {
    suncopro: 'SuncoPRO',
    commercial: 'Commercial',
    residential: 'Residential',
    outdoor: 'Outdoor',
    electrical: 'Electrical',
    deals: 'Deals',
  };

  if (titleOverrides[normalized]) return titleOverrides[normalized];

  return normalized
    .split('-')
    .filter(Boolean)
    .map((part) => {
      if (/^\d+k$/i.test(part)) return part.toUpperCase();
      if (['led', 'ufo', 'cct', 'cri', 'rgb', 'br', 'par'].includes(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
}

function collectionFromUrl(value) {
  if (!value) return null;

  try {
    const url = new URL(value, window.location.href);
    if (url.origin !== window.location.origin) return null;

    const match = url.pathname.match(/^\/collections\/([^/?#]+)/);
    if (!match?.[1] || match[1] === 'all') return null;

    const handle = decodeURIComponent(match[1]);
    return {
      handle,
      title: humanizeCollectionHandle(handle),
      url: `/collections/${encodeURIComponent(handle)}`,
    };
  } catch (_error) {
    return null;
  }
}

function normalizeCollectionUrl(value) {
  try {
    return new URL(value, window.location.href).pathname.replace(/\/$/, '');
  } catch (_error) {
    return String(value || '').split('?')[0].replace(/\/$/, '');
  }
}

function normalizeSessionCollection(collection) {
  if (!collection?.title || !collection?.url) return null;
  return {
    title: String(collection.title).trim(),
    url: normalizeCollectionUrl(collection.url),
  };
}

function sameSessionCollection(first, second) {
  return normalizeCollectionUrl(first?.url) === normalizeCollectionUrl(second?.url);
}

function dedupeSessionCollectionTrail(trail) {
  const output = [];

  trail.map(normalizeSessionCollection).filter(Boolean).forEach((collection) => {
    const existingIndex = output.findIndex((item) => sameSessionCollection(item, collection));
    if (existingIndex >= 0) output.splice(existingIndex + 1);
    else output.push(collection);
  });

  return output;
}

function getStoredSessionCollection() {
  try {
    return normalizeSessionCollection(JSON.parse(window.sessionStorage.getItem(suncoSessionCollectionKey) || 'null'));
  } catch (_error) {
    return null;
  }
}

function getStoredSessionCollectionTrail() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(suncoSessionCollectionTrailKey) || '[]');
    return Array.isArray(stored) ? dedupeSessionCollectionTrail(stored) : [];
  } catch (_error) {
    return [];
  }
}

function setStoredSessionCollection(collection) {
  const normalizedCollection = normalizeSessionCollection(collection);
  if (!normalizedCollection) return;

  try {
    window.sessionStorage.setItem(suncoSessionCollectionKey, JSON.stringify(normalizedCollection));
  } catch (_error) {
    // Session storage can be unavailable in strict privacy modes; the static breadcrumb remains usable.
  }
}

function setStoredSessionCollectionTrail(trail) {
  const normalizedTrail = dedupeSessionCollectionTrail(trail);

  try {
    window.sessionStorage.setItem(suncoSessionCollectionTrailKey, JSON.stringify(normalizedTrail));
    if (normalizedTrail.at(-1)) {
      window.sessionStorage.setItem(suncoSessionCollectionKey, JSON.stringify(normalizedTrail.at(-1)));
    }
  } catch (_error) {
    // Session storage can be unavailable in strict privacy modes; the static breadcrumb remains usable.
  }

  return normalizedTrail;
}

function clearStoredSessionCollection() {
  try {
    window.sessionStorage.removeItem(suncoSessionCollectionKey);
    window.sessionStorage.removeItem(suncoSessionCollectionTrailKey);
  } catch (_error) {
    // Ignore storage failures and keep the static breadcrumb.
  }
}

function collectionFromBreadcrumb(root = document) {
  const link = root.querySelector('[data-sunco-breadcrumb-collection]');
  const title = link?.textContent?.trim();
  const url = link?.getAttribute('href');
  if (!title || !url) return null;

  return normalizeSessionCollection({ title, url });
}

function isExternalReferrer() {
  if (!document.referrer) return false;

  try {
    return new URL(document.referrer).origin !== window.location.origin;
  } catch (_error) {
    return false;
  }
}

function appendBreadcrumbSeparator(nav) {
  const separator = document.createElement('span');
  separator.setAttribute('aria-hidden', 'true');
  separator.textContent = '/';
  nav.append(separator);
}

function collectionTrailForProduct(referrerCollection, currentCollection, storedCollection, storedTrail) {
  if (referrerCollection) {
    const referrerIndex = storedTrail.findIndex((collection) => sameSessionCollection(collection, referrerCollection));
    return referrerIndex >= 0 ? storedTrail.slice(0, referrerIndex + 1) : [referrerCollection];
  }

  if (currentCollection) {
    const currentIndex = storedTrail.findIndex((collection) => sameSessionCollection(collection, currentCollection));
    return currentIndex >= 0 ? storedTrail.slice(0, currentIndex + 1) : [currentCollection];
  }

  if (storedTrail.length > 0) return storedTrail;
  return storedCollection ? [storedCollection] : [];
}

function renderSessionBreadcrumb(nav, collectionTrail) {
  const homeHref = nav.querySelector('[data-sunco-breadcrumb-home]')?.getAttribute('href') || '/';
  const productTitle = nav.querySelector('[data-sunco-breadcrumb-product]')?.textContent?.trim();
  if (!productTitle) return;

  const trail = dedupeSessionCollectionTrail(Array.isArray(collectionTrail) ? collectionTrail : [collectionTrail]);
  nav.textContent = '';

  const home = document.createElement('a');
  home.href = homeHref;
  home.dataset.suncoBreadcrumbHome = '';
  home.textContent = 'Home';
  nav.append(home);

  appendBreadcrumbSeparator(nav);

  trail.forEach((collection) => {
    const collectionLink = document.createElement('a');
    collectionLink.href = collection.url;
    collectionLink.dataset.suncoBreadcrumbCollection = '';
    collectionLink.textContent = collection.title;
    nav.append(collectionLink);
    appendBreadcrumbSeparator(nav);
  });

  const product = document.createElement('span');
  product.dataset.suncoBreadcrumbProduct = '';
  product.textContent = productTitle;
  nav.append(product);
}

function setupSessionBreadcrumb(root = document) {
  const breadcrumbs = Array.from(root.querySelectorAll('[data-sunco-breadcrumb]'));
  if (breadcrumbs.length === 0) return;

  if (isExternalReferrer()) clearStoredSessionCollection();

  const referrerCollection = collectionFromUrl(document.referrer);
  const currentCollection = collectionFromBreadcrumb(root);
  const storedCollection = getStoredSessionCollection();
  const storedTrail = getStoredSessionCollectionTrail();
  const collectionTrail = collectionTrailForProduct(referrerCollection, currentCollection, storedCollection, storedTrail);

  if (collectionTrail.length > 0) {
    setStoredSessionCollection(collectionTrail.at(-1));
    setStoredSessionCollectionTrail(collectionTrail);
  }

  breadcrumbs.forEach((breadcrumb) => renderSessionBreadcrumb(breadcrumb, collectionTrail));
}

function setupStampedPhotoCarousel(root = document) {
  const initialize = (summary) => {
    if (!(summary instanceof HTMLElement) || summary.dataset.suncoCarouselReady === 'true') return;

    const carousel = summary.querySelector('.stamped-photos-carousel');
    const track = carousel?.firstElementChild;
    if (!(carousel instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

    const photos = Array.from(track.querySelectorAll('.photo.stamped-photo')).filter(
      (photo) => photo instanceof HTMLElement
    );
    if (photos.length === 0) return;

    photos.forEach((photo) => {
      if (photo.dataset.suncoPhotoZoomReady === 'true') return;

      const openPhoto = (event) => {
        const target = event.target;
        if (target instanceof Element && target.closest('img[onclick]')) return;

        const image = photo.querySelector('img[onclick]');
        if (!(image instanceof HTMLElement)) return;

        event.preventDefault();
        event.stopPropagation();
        image.click();
      };

      photo.dataset.suncoPhotoZoomReady = 'true';
      photo.setAttribute('role', 'button');
      photo.setAttribute('tabindex', '0');
      photo.addEventListener('click', openPhoto);
      photo.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        openPhoto(event);
      });
    });

    if (photos.length <= 1) return;

    summary.dataset.suncoCarouselReady = 'true';
    let index = 0;

    const visiblePhotos = () => photos.filter((photo) => getComputedStyle(photo).display !== 'none');

    const stepSize = () => {
      const firstPhoto = visiblePhotos()[0];
      if (!(firstPhoto instanceof HTMLElement)) return 0;

      const trackStyle = getComputedStyle(track);
      const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;
      return firstPhoto.getBoundingClientRect().width + gap;
    };

    const visibleCount = () => {
      const step = stepSize();
      if (!step) return 1;

      const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
      return Math.max(1, Math.floor((carousel.getBoundingClientRect().width + gap) / step));
    };

    const update = () => {
      const maxIndex = Math.max(0, visiblePhotos().length - visibleCount());
      index = Math.min(Math.max(index, 0), maxIndex);
      track.style.transform = `translateX(${-index * stepSize()}px)`;
      summary.classList.toggle('sunco-stamped-carousel-at-start', index === 0);
      summary.classList.toggle('sunco-stamped-carousel-at-end', index === maxIndex);
    };

    summary.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.stamped-photos-carousel-btn-left, .stamped-photos-carousel-btn-right');
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();
        index += button.classList.contains('stamped-photos-carousel-btn-right') ? 1 : -1;
        update();
      },
      true
    );

    window.addEventListener('resize', update);
    update();
  };

  root.querySelectorAll('#stamped-main-widget .stamped-summary').forEach(initialize);

  if (document.documentElement.dataset.suncoStampedCarouselObserver === 'true') return;
  document.documentElement.dataset.suncoStampedCarouselObserver = 'true';

  let observerFrame = 0;
  let observerTarget = null;
  const observer = new MutationObserver(() => {
    if (observerFrame) return;
    observerFrame = window.requestAnimationFrame(() => {
      observerFrame = 0;
      document.querySelectorAll('#stamped-main-widget .stamped-summary').forEach(initialize);

      const widget = document.querySelector('#stamped-main-widget');
      if (widget instanceof HTMLElement && observerTarget !== widget) {
        observer.disconnect();
        observerTarget = widget;
        observer.observe(widget, { childList: true, subtree: true });
      }
    });
  });
  const initialWidget = document.querySelector('#stamped-main-widget');
  observerTarget = initialWidget instanceof HTMLElement ? initialWidget : document.body;
  observer.observe(
    observerTarget,
    observerTarget === document.body ? { childList: true } : { childList: true, subtree: true }
  );
}

function setupStampedAccessibility(root = document) {
  const normalize = () => {
    document.querySelectorAll('.stamped-badge[aria-label], .stamped-product-reviews-badge[aria-label]').forEach((badge) => {
      if (badge instanceof HTMLElement && !badge.hasAttribute('role')) {
        badge.setAttribute('role', 'img');
      }
    });

    document.querySelectorAll('#stamped-main-widget .stamped-review-header-title').forEach((heading) => {
      if (!(heading instanceof HTMLElement)) return;
      if (heading.textContent.trim() !== '' || heading.querySelector('img, svg')) return;

      if (!heading.hidden) heading.hidden = true;
      if (heading.getAttribute('aria-hidden') !== 'true') heading.setAttribute('aria-hidden', 'true');
    });
  };

  normalize();

  if (document.documentElement.dataset.suncoStampedAccessibilityObserver === 'true') return;
  document.documentElement.dataset.suncoStampedAccessibilityObserver = 'true';

  let observerFrame = 0;
  let observerTarget = null;
  const observer = new MutationObserver(() => {
    if (observerFrame) return;
    observerFrame = window.requestAnimationFrame(() => {
      observerFrame = 0;
      normalize();

      const widget = document.querySelector('#stamped-main-widget');
      if (widget instanceof HTMLElement && observerTarget !== widget) {
        observer.disconnect();
        observerTarget = widget;
        observer.observe(widget, { attributeFilter: ['aria-label'], attributes: true, childList: true, subtree: true });
      }
    });
  });
  const initialWidget = document.querySelector('#stamped-main-widget');
  observerTarget = initialWidget instanceof HTMLElement ? initialWidget : document.body;
  observer.observe(
    observerTarget,
    observerTarget === document.body
      ? { childList: true }
      : { attributeFilter: ['aria-label'], attributes: true, childList: true, subtree: true }
  );
}

function setupStampedReviewJump() {
  if (document.documentElement.dataset.suncoStampedReviewJumpReady === 'true') return;
  document.documentElement.dataset.suncoStampedReviewJumpReady = 'true';

  let restoreTimer = 0;
  let previousInlineBehavior = '';

  const prepareReviewJump = () => {
    const root = document.documentElement;
    const reviewWidget = document.querySelector('#stamped-main-widget');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!restoreTimer) previousInlineBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';

    if (reduceMotion && reviewWidget instanceof HTMLElement) {
      const previousAnimation = reviewWidget.getAttribute('data-animation');
      reviewWidget.setAttribute('data-animation', 'false');
      window.scrollTo(0, reviewWidget.getBoundingClientRect().top + window.scrollY);

      window.setTimeout(() => {
        if (previousAnimation === null) reviewWidget.removeAttribute('data-animation');
        else reviewWidget.setAttribute('data-animation', previousAnimation);
      }, 0);
    }

    window.clearTimeout(restoreTimer);
    restoreTimer = window.setTimeout(() => {
      root.style.scrollBehavior = previousInlineBehavior;
      previousInlineBehavior = '';
      restoreTimer = 0;
    }, reduceMotion ? 50 : 250);
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!event.target?.closest?.('[data-sunco-review-jump]')) return;
      prepareReviewJump();
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const trigger = event.target?.closest?.('[data-sunco-review-jump]');
      if (!(trigger instanceof HTMLElement)) return;

      event.preventDefault();

      const reviewWidget = document.querySelector('#stamped-main-widget');
      const previousAnimation = reviewWidget?.getAttribute('data-animation');

      reviewWidget?.setAttribute('data-animation', 'false');
      trigger.click();

      if (reviewWidget instanceof HTMLElement) {
        window.scrollTo(0, reviewWidget.getBoundingClientRect().top + window.scrollY);

        window.setTimeout(() => {
          if (previousAnimation === null) reviewWidget.removeAttribute('data-animation');
          else reviewWidget.setAttribute('data-animation', previousAnimation);
        }, 0);
      }
    },
    true
  );
}

function setupMobileSpecTables(root = document) {
  root.querySelectorAll('[data-sunco-spec-disclosure]').forEach((component) => {
    if (!(component instanceof HTMLElement) || component.dataset.suncoSpecReady === 'true') return;

    const button = component.querySelector('[data-sunco-spec-toggle]');
    const rows = component.querySelectorAll('.sunco-spec-table tbody tr');
    if (!(button instanceof HTMLButtonElement) || rows.length <= 10) return;

    component.dataset.suncoSpecReady = 'true';
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    let expanded = false;

    const render = () => {
      const active = mobileQuery.matches;
      component.classList.toggle('is-collapsible', active);
      component.classList.toggle('is-expanded', !active || expanded);
      button.hidden = !active;
      button.setAttribute('aria-expanded', String(!active || expanded));
    };

    button.addEventListener('click', () => {
      expanded = !expanded;
      render();
      if (!expanded && mobileQuery.matches) {
        component.closest('.sunco-pdp-content__section')?.scrollIntoView({ block: 'start' });
      }
    });

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', render);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(render);
    }

    render();
  });
}

function setupLayoutToolFrames(root = document) {
  root.querySelectorAll('.sunco-layout-tool-dialog').forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.suncoLayoutReady === 'true') return;

    const component = dialog.closest('dialog-component');
    const frame = dialog.querySelector('[data-sunco-layout-frame]');
    if (!(component instanceof HTMLElement) || !(frame instanceof HTMLIFrameElement)) return;

    dialog.dataset.suncoLayoutReady = 'true';
    component.addEventListener('dialog:open', () => {
      const src = frame.dataset.src;
      if (!src || frame.src) return;

      requestAnimationFrame(() => {
        frame.src = src;
      });
    });
  });
}

function updateLegacyYoutubeMask(media) {
  if (!(media instanceof HTMLElement)) return;

  const slide = media.closest('.product-media-container');
  if (!(slide instanceof HTMLElement)) return;

  const rect = slide.getBoundingClientRect();
  const maskSize = Math.max((rect.height - (rect.width * 9) / 16) / 2, 0);
  slide.classList.add('sunco-youtube-mask');
  slide.style.setProperty('--sunco-youtube-mask-size', `${maskSize}px`);
}

function clearLegacyYoutubeMask(media) {
  if (!(media instanceof HTMLElement)) return;

  const slide = media.closest('.product-media-container');
  if (!(slide instanceof HTMLElement)) return;

  slide.classList.remove('sunco-youtube-mask');
  slide.style.removeProperty('--sunco-youtube-mask-size');
}

function playLegacyYoutubeMedia(media) {
  if (!(media instanceof HTMLElement)) return;

  if (!media.hasAttribute('data-media-loaded')) {
    if (typeof media.showDeferredMedia === 'function') {
      media.showDeferredMedia();
      return;
    }

    media.querySelector('[ref="deferredMediaPlayButton"]')?.click();
    return;
  }

  if (typeof media.playMedia === 'function') {
    media.playMedia();
    return;
  }

  media.querySelector('iframe[data-video-type="youtube"]')?.contentWindow?.postMessage(
    '{"event":"command","func":"playVideo","args":""}',
    '*'
  );
}

function pauseLegacyYoutubeMedia(media) {
  if (!(media instanceof HTMLElement) || !media.hasAttribute('data-media-loaded')) return;

  if (typeof media.pauseMedia === 'function') {
    media.pauseMedia();
    return;
  }

  media.querySelector('iframe[data-video-type="youtube"]')?.contentWindow?.postMessage(
    '{"event":"command","func":"pauseVideo","args":""}',
    '*'
  );
}

function syncLegacyYoutubeGalleryMedia(gallery, selectedSlide = null) {
  if (!(gallery instanceof HTMLElement)) return;

  const activeSlide =
    selectedSlide ||
    gallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
    gallery.querySelector('.product-media-container[aria-hidden="false"]');
  const activeMedia = activeSlide?.querySelector('deferred-media[data-sunco-youtube-media]');

  gallery.querySelectorAll('deferred-media[data-sunco-youtube-media]').forEach((media) => {
    if (media === activeMedia) {
      updateLegacyYoutubeMask(media);
      playLegacyYoutubeMedia(media);
    } else {
      clearLegacyYoutubeMask(media);
      pauseLegacyYoutubeMedia(media);
    }
  });
}

function setupLegacyYoutubeGalleryMedia(root = document) {
  root.querySelectorAll('media-gallery').forEach((gallery) => {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.suncoYoutubeGalleryReady === 'true') return;

    gallery.dataset.suncoYoutubeGalleryReady = 'true';
    gallery.addEventListener('slideshow:select', (event) => {
      window.requestAnimationFrame(() => {
        syncLegacyYoutubeGalleryMedia(gallery, event.detail?.slide);
      });
    });

    const observer = new MutationObserver(() => {
      syncLegacyYoutubeGalleryMedia(gallery);
    });
    observer.observe(gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  });

  if (document.documentElement.dataset.suncoYoutubeGalleryEventsReady === 'true') return;
  document.documentElement.dataset.suncoYoutubeGalleryEventsReady = 'true';

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    window.requestAnimationFrame(() => {
      setupLegacyYoutubeGalleryMedia(event.target?.closest?.('.shopify-section') || document);
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    document.querySelectorAll('deferred-media[data-sunco-youtube-media]').forEach(pauseLegacyYoutubeMedia);
  });

  window.addEventListener('resize', () => {
    document
      .querySelectorAll('.product-media-container.sunco-youtube-mask deferred-media[data-sunco-youtube-media]')
      .forEach(updateLegacyYoutubeMask);
  });
}

function setupVerticalDescriptionContent(root = document) {
  root.querySelectorAll('.sunco-vertical-description .sunco-pdp-content__body > p').forEach((paragraph) => {
    if (!(paragraph instanceof HTMLElement)) return;

    const text = (paragraph.textContent || '').replace(/\u00a0/g, ' ').trim();
    const hasMedia = paragraph.querySelector('img, picture, video, audio, iframe, svg, canvas, object, embed');
    if (!text && !hasMedia) paragraph.hidden = true;
  });

  root
    .querySelectorAll('main[data-template="product.electrical-side-desc"] .sunco-vertical-description .sunco-pdp-content__body')
    .forEach((body) => {
      if (!(body instanceof HTMLElement)) return;

      body.querySelectorAll('.sunco-compatible-fixture-chip').forEach((chip) => {
        chip.classList.remove('sunco-compatible-fixture-chip');
      });
      body.querySelectorAll('.sunco-compatible-fixture-table').forEach((table) => {
        table.classList.remove('sunco-compatible-fixture-table');
      });

      body.querySelectorAll('h3, h4, h5').forEach((heading) => {
        if (!(heading instanceof HTMLElement)) return;

        const headingText = (heading.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (!headingText.includes('compatible fixture')) return;

        let node = heading.nextElementSibling;
        while (node instanceof HTMLElement && !/^(H3|H4|H5)$/i.test(node.tagName)) {
          const text = (node.textContent || '').replace(/\u00a0/g, ' ').trim();
          if (node.tagName === 'P' && text) {
            node.classList.add('sunco-compatible-fixture-chip');
          }
          if (node.tagName === 'TABLE' && text) {
            node.classList.add('sunco-compatible-fixture-table');
          }
          node = node.nextElementSibling;
        }
      });
    });
}

function getCertificationImageAlt(image) {
  const source = String(image.currentSrc || image.src || '').toLowerCase();
  const certificationNames = [
    [/1773684537074/, 'DLC certification'],
    [/1773684556263/, 'FCC certification'],
    [/energy[\s_-]*star/, 'ENERGY STAR certification'],
    [/(^|[\s/_-])etl([\s/_.-]|$)/, 'ETL certification'],
    [/(^|[\s/_-])dlc([\s/_.-]|$)/, 'DLC certification'],
    [/(^|[\s/_-])fcc([\s/_.-]|$)/, 'FCC certification'],
    [/(^|[\s/_-])rohs([\s/_.-]|$)/, 'RoHS compliance'],
    [/(^|[\s/_-])ul([\s/_.-]|$)/, 'UL certification'],
    [/(^|[\s/_-])ce([\s/_.-]|$)/, 'CE certification'],
    [/(american|us[\s_-]*company|usa[\s_-]*company)/, 'American company'],
  ];

  return certificationNames.find(([pattern]) => pattern.test(source))?.[1] || '';
}

function setupPdpContentImageAlts(root = document) {
  root.querySelectorAll('[data-sunco-pdp-image-context]').forEach((content) => {
    if (!(content instanceof HTMLElement)) return;

    const owner = content.closest('[data-sunco-product-title]');
    const productTitle = owner?.dataset.suncoProductTitle?.trim() || 'Product';
    const context = content.dataset.suncoPdpImageContext?.trim() || 'Product details';
    const isCertification = context.toLowerCase().includes('cert');

    content.querySelectorAll('img').forEach((image, index) => {
      if (!(image instanceof HTMLImageElement)) return;
      if ((image.getAttribute('alt') || '').trim()) return;
      if (image.getAttribute('role') === 'presentation' || image.getAttribute('aria-hidden') === 'true') return;

      const certificationAlt = isCertification ? getCertificationImageAlt(image) : '';
      image.alt = certificationAlt ||
        (isCertification
          ? `${productTitle} certification logo ${index + 1}`
          : `${productTitle} ${context.toLowerCase()} image ${index + 1}`);
    });
  });

  if (document.documentElement.dataset.suncoPdpContentImageAltsReady === 'true') return;
  document.documentElement.dataset.suncoPdpContentImageAltsReady = 'true';

  document.addEventListener('shopify:section:load', (event) => {
    setupPdpContentImageAlts(event.target || document);
  });
}

function setupFrequentlyBoughtTogetherAnchor() {
  const anchor = document.querySelector('[data-sunco-fbt-anchor]');
  if (!(anchor instanceof HTMLElement) || anchor.dataset.suncoFbtAnchorReady === 'true') return;

  anchor.dataset.suncoFbtAnchorReady = 'true';
  let observer = null;
  let observerFrame = 0;
  let observerTimeout = 0;
  let cartSyncRun = 0;

  const renderedCartCount = () => {
    const cartIcon = document.querySelector('cart-icon');
    const componentCount = Number(cartIcon?.currentCartCount);
    if (Number.isFinite(componentCount)) return componentCount;

    const countText = cartIcon?.querySelector('[ref="cartBubbleCount"]')?.textContent?.trim();
    const textCount = Number(countText);
    return Number.isFinite(textCount) ? textCount : 0;
  };

  const syncCartAfterFbtAdd = async (button) => {
    const run = ++cartSyncRun;
    const sourceId = button.id || 'sunco-fbt-add-selected';
    let observedCount = renderedCartCount();
    let didSync = false;

    for (let attempt = 0; attempt < 12; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 250 : 300));
      if (run !== cartSyncRun) return;

      try {
        const response = await fetch('/cart.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) continue;

        const cart = await response.json();
        const itemCount = Number(cart.item_count);
        if (!Number.isFinite(itemCount)) continue;

        if (itemCount === observedCount) {
          if (didSync) return;
          continue;
        }

        observedCount = itemCount;
        didSync = true;
        document.dispatchEvent(
          new CartAddEvent(cart, sourceId, {
            source: 'sunco-fbt-add-selected',
            itemCount,
          })
        );
      } catch (_error) {
        // The app owns add-to-cart errors; retry only the cart-count readback.
      }
    }
  };

  anchor.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.cbb-frequently-bought-add-button');
    if (!(button instanceof HTMLButtonElement) || !anchor.contains(button)) return;

    syncCartAfterFbtAdd(button);
  });

  const mount = () => {
    let mounted = false;
    document.querySelectorAll('.cbb-frequently-bought-container').forEach((container) => {
      if (!(container instanceof HTMLElement) || anchor.contains(container)) return;

      anchor.append(container);
      container.classList.add('sunco-fbt-mounted');
      mounted = true;
    });

    const section = anchor.closest('[data-sunco-fbt-anchor-section]');
    section?.classList.toggle(
      'sunco-fbt-anchor-section--has-content',
      anchor.querySelector('.cbb-frequently-bought-container') !== null
    );

    return mounted;
  };

  const stopObserving = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (observerTimeout) {
      window.clearTimeout(observerTimeout);
      observerTimeout = 0;
    }
  };

  const scheduleMount = () => {
    if (observerFrame) return;
    observerFrame = window.requestAnimationFrame(() => {
      observerFrame = 0;

      if (mount()) {
        stopObserving();
      }
    });
  };

  if (mount()) return;

  observer = new MutationObserver(scheduleMount);
  observer.observe(document.body, { childList: true });

  observerTimeout = window.setTimeout(stopObserving, 8000);

  document.addEventListener(
    ThemeEvents.variantUpdate,
    () => {
      if (!anchor.querySelector('.cbb-frequently-bought-container')) {
        scheduleMount();
      }
    },
    { passive: true }
  );
}

function setupLinkedOptionsPlacement() {
  if (document.documentElement.dataset.suncoLinkedOptionsPlacementReady === 'true') return;
  document.documentElement.dataset.suncoLinkedOptionsPlacementReady = 'true';

  let observer = null;
  let observerFrame = 0;
  let observerTimeout = 0;

  const stopObserving = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (observerTimeout) {
      window.clearTimeout(observerTimeout);
      observerTimeout = 0;
    }
  };

  const placeLinkedOptions = () => {
    const linkedOptions = document.querySelector('#king-linked-options-section');
    if (!(linkedOptions instanceof HTMLElement)) return false;

    // LinkedOption can fall back inside the buy-buttons block when a duplicated
    // theme ID is not present in the app's external Horizon placement config.
    if (!linkedOptions.closest('.buy-buttons-block')) return true;

    const productDetails = linkedOptions.closest('.product-details');
    const variantPicker = productDetails?.querySelector('variant-picker');
    const destination = variantPicker?.parentElement;

    if (!(variantPicker instanceof HTMLElement) || !(destination instanceof HTMLElement)) return false;

    destination.insertBefore(linkedOptions, variantPicker);
    linkedOptions.dataset.suncoLinkedOptionsPlaced = 'true';
    return true;
  };

  const schedulePlacement = () => {
    if (observerFrame) return;

    observerFrame = window.requestAnimationFrame(() => {
      observerFrame = 0;
      if (placeLinkedOptions()) stopObserving();
    });
  };

  const observeUntilPlaced = () => {
    if (placeLinkedOptions() || observer) return;

    observer = new MutationObserver(schedulePlacement);
    observer.observe(document.body, { childList: true, subtree: true });
    observerTimeout = window.setTimeout(stopObserving, 8000);
  };

  observeUntilPlaced();

  document.addEventListener(ThemeEvents.variantUpdate, schedulePlacement, { passive: true });
  document.addEventListener('shopify:section:load', observeUntilPlaced, { passive: true });
}

function setupSelleasyCartSync() {
  if (document.documentElement.dataset.suncoSelleasyCartSyncReady === 'true') return;
  document.documentElement.dataset.suncoSelleasyCartSyncReady = 'true';

  let cartSyncRun = 0;

  const renderedCartCount = () => {
    const cartIcon = document.querySelector('cart-icon');
    const componentCount = Number(cartIcon?.currentCartCount);
    if (Number.isFinite(componentCount)) return componentCount;

    const countText = cartIcon?.querySelector('[ref="cartBubbleCount"]')?.textContent?.trim();
    const textCount = Number(countText);
    return Number.isFinite(textCount) ? textCount : 0;
  };

  const syncCartAfterSelleasyAdd = async (button) => {
    const run = ++cartSyncRun;
    const variantId = button.closest('[lb-variant-id]')?.getAttribute('lb-variant-id');
    const sourceId = variantId ? `sunco-selleasy-addon-${variantId}` : 'sunco-selleasy-addon';
    let observedCount = renderedCartCount();
    let didSync = false;

    for (let attempt = 0; attempt < 12; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 250 : 300));
      if (run !== cartSyncRun) return;

      try {
        const response = await fetch('/cart.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) continue;

        const cart = await response.json();
        const itemCount = Number(cart.item_count);
        if (!Number.isFinite(itemCount)) continue;

        if (itemCount === observedCount) {
          if (didSync) return;
          continue;
        }

        observedCount = itemCount;
        didSync = true;
        document.dispatchEvent(
          new CartAddEvent(cart, sourceId, {
            source: 'sunco-selleasy-addon',
            itemCount,
          })
        );
      } catch (_error) {
        // Selleasy owns add-to-cart errors; retry only the cart-count readback.
      }
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('lb-upsell-widget lb-button[lb-action-product]');
      if (!(button instanceof HTMLElement)) return;

      syncCartAfterSelleasyAdd(button);
    },
    { capture: true }
  );
}

function setupZoomableProductMediaKeyboard() {
  if (document.documentElement.dataset.suncoZoomKeyboardReady === 'true') return;
  document.documentElement.dataset.suncoZoomKeyboardReady = 'true';

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const slide = event.target?.closest?.(
      'slideshow-slide.product-media-container--zoomable[role="button"]:not([inert])'
    );
    if (!(slide instanceof HTMLElement)) return;

    event.preventDefault();
    slide.click();
  });
}

setupGroupedAddons();
setupCombinedListingNativePicker();
setupColorSwatchVisualState();
setupVariantSelectedLabels();
setupVariantAnnouncements();
setupQuantitySteppers();
setupBackInStock();
setupCartDrawerAfterProductAdd();
setupStickyOverlayOffsets();
setupSkuCopy();
setupBulkQuoteForm();
setupDiscountLearnMore();
setupUnitPrice();
setupPdpSubtotal();
setupRecentlyViewed();
setupSessionBreadcrumb();
setupStampedPhotoCarousel();
setupStampedAccessibility();
setupStampedReviewJump();
setupMobileSpecTables();
setupLayoutToolFrames();
setupLegacyYoutubeGalleryMedia();
setupVerticalDescriptionContent();
setupPdpContentImageAlts();
setupLinkedOptionsPlacement();
setupFrequentlyBoughtTogetherAnchor();
setupSelleasyCartSync();
setupZoomableProductMediaKeyboard();
